import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Logo } from '@/components/brand/Logo';
import { peekNext } from '@/lib/auth/next';
import { useAuth } from '@/lib/auth/useAuth';
import { useRequireAuth } from '@/lib/auth/useRequireAuth';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/onboarding/welcome')({
  component: WelcomeRoute,
});

function WelcomeRoute() {
  const { user, refreshProfile } = useRequireAuth();
  useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState<'subscriber' | 'creator' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const checkoutWaiting = peekNext().startsWith('/subscribe');

  const choose = async (path: 'subscriber' | 'creator') => {
    if (!user) return;
    setSaving(path);
    setError(null);
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ onboarding_path: path })
      .eq('id', user.id);
    if (updateError) {
      setError(updateError.message);
      setSaving(null);
      return;
    }
    await refreshProfile();
    void navigate({ to: path === 'subscriber' ? '/onboarding/subscriber' : '/onboarding/creator' });
  };

  return (
    <div className="min-h-screen bg-parchment">
      <header className="px-6 py-6">
        <Link to="/" aria-label="ArcheTelos" className="inline-block">
          <Logo className="h-9 w-auto" />
        </Link>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="font-serif text-3xl text-ink balance sm:text-4xl">Welcome.</h1>
        <p className="mt-4 font-sans text-lg text-ink/80 pretty">
          Two ways to start. You can change later — neither closes a door.
        </p>
        {checkoutWaiting && (
          <p className="mt-4 font-sans text-sm text-ink/60">
            Your checkout is waiting — this takes ten seconds.
          </p>
        )}

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <PathCard
            label="Read"
            description="Essays, video, and live programming. A subscription unlocks the archive and every broadcast. Most readers start here."
            ctaLabel={saving === 'subscriber' ? 'Saving…' : 'Continue as a reader'}
            onClick={() => void choose('subscriber')}
            disabled={saving !== null}
          />
          <PathCard
            label="Contribute"
            description="If you write, film, photograph, or research in any of the traditions we cover, apply to become a contributor. Applications are reviewed by editorial; expect a response within a week."
            ctaLabel={saving === 'creator' ? 'Saving…' : 'Apply as a contributor'}
            onClick={() => void choose('creator')}
            disabled={saving !== null}
          />
        </div>

        {error && (
          <p role="alert" aria-live="polite" className="mt-6 text-sm text-signal">
            {error}
          </p>
        )}

        <p className="mt-10 text-center text-sm text-ink/60">
          Not sure yet?{' '}
          <button
            type="button"
            onClick={() => void choose('subscriber')}
            className="text-oxblood hover:underline"
          >
            Default to reader — you can apply as a contributor any time.
          </button>
        </p>
      </main>
    </div>
  );
}

interface PathCardProps {
  label: string;
  description: string;
  ctaLabel: string;
  onClick: () => void;
  disabled?: boolean;
}
function PathCard({ label, description, ctaLabel, onClick, disabled }: PathCardProps) {
  return (
    <article className="flex flex-col rounded-lg border border-ink/10 bg-parchment p-6 shadow-sm">
      <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">{label}</p>
      <p className="mt-4 font-sans text-base text-ink/80 pretty">{description}</p>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="mt-8 w-full rounded-md bg-ink px-4 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
      >
        {ctaLabel}
      </button>
    </article>
  );
}
